import * as React from 'react'
import useDriverService from '../services/DriverService'
import { GroupModel } from '../types/groups'
import { DriverModel } from '@/app/models'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import useGroupService from '../services/GroupService'

type GroupType = 'custom' | 'default' | null
type EditGroupsFormContextType = {
  driversInGroup: number[]
  selectedGroup: number[]
  isSelectedAny: boolean
  addedIds: number[]
  removedIds: number[]
  toggleAll: (checked: boolean) => void
  toggleId: (id: number) => void
  confirmEditGroupMembers: () => Promise<void>

  loading: boolean
  setGroupType: React.Dispatch<GroupType>
}

const EditGroupsFormContext = React.createContext<
  EditGroupsFormContextType | undefined
>(undefined)

export const EditGroupsFormProvider = ({
  children,
  companyId,
  group,
}: {
  children: React.ReactNode
  drivers: DriverModel[]
  companyId: string
  group: GroupModel
}) => {
  const { t } = useLocalize()
  const { getDrivers } = useDriverService()
  const { getDriversInGroup, activateDriver } = useDriverService()
  const { addDriverToGroup, removeDriverFromGroup } = useGroupService()
  const [loading, setLoading] = React.useState(false)
  const [driversInGroup, setDriversInGroup] = React.useState<number[]>([])
  const [selectedGroup, setSelectedGroup] = React.useState<number[]>([])
  const [initialIds, setInitialIds] = React.useState<number[]>([])
  const [groupType, setGroupType] = React.useState<GroupType>(null)

  const addedIds = React.useMemo(
    () => selectedGroup.filter((id) => !initialIds.includes(id)),
    [selectedGroup, initialIds],
  )

  const removedIds = React.useMemo(
    () => initialIds.filter((id) => !selectedGroup.includes(id)),
    [selectedGroup, initialIds],
  )

  const fetchDriversInGroup = async () => {
    setLoading(true)
    if (group.id < 0) {
      const drivers = await getDrivers({ companyId: companyId })
      const filtered = drivers.filter(
        (driver) => driver?.isActive === (group.id === -1),
      )
      const ids = filtered.map((d) => d.id)
      setDriversInGroup(filtered.map((d) => d.id))
      setInitialIds(filtered.map((d) => d.id))
      setLoading(false)
      return
    }

    getDriversInGroup({ companyId, groupId: group.id })
      .then((res) => {
        const ids = res.map((d) => d)
        setDriversInGroup(ids)
        setInitialIds(ids)
      })
      .catch(() => {
        toast.error({ text: t('driverModule.error-fetching-group-drivers') })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const toggleId = (id: number) => {
    setSelectedGroup((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id)
      }
      return [...prev, id]
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedGroup(driversInGroup)
    } else {
      setSelectedGroup([])
    }
  }

  const confirmEditGroupMembers = async () => {
    setLoading(true)
    const isAnyAdded = addedIds.length > 0
    const isAnyRemoved = removedIds.length > 0

    if (!isAnyAdded && !isAnyRemoved) {
      toast.info({ text: t('driverModule.no-changes-to-save') })
      setLoading(false)
      return
    }
    if (!groupType) {
      toast.error({ text: t('driverModule.unknown-group-type') })
      setLoading(false)
      return
    }
    try {
      if (groupType === 'custom') {
        const groupId = String(group.id) as string
        if (isAnyRemoved) {
          await removeDriverFromGroup({
            groupId: groupId,
            driverIds: removedIds,
            companyId: companyId,
          })
        }
        if (isAnyAdded) {
          await addDriverToGroup({
            groupId: groupId,
            driverIds: addedIds,
            companyId: companyId,
          })
        }
      } else if (groupType === 'default') {
        if (isAnyAdded) {
          await activateDriver({
            companyId,
            driversIds: addedIds,
            setActive: true,
          })
        }
        if (isAnyRemoved) {
          await activateDriver({
            companyId,
            driversIds: removedIds,
            setActive: false,
          })
        }
      }
      toast.success({ text: t('driverModule.group-members-updated') })
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      toast.error({
        text: code === 'DRIVER_GROUP_REQUIRED'
          ? t('driverModule.group-required')
          : t('driverModule.error-updating-group-members'),
      })
    } finally {
      setLoading(false)
      await fetchDriversInGroup()
    }
  }

  React.useEffect(() => {
    if (group && group.id > 0) {
      setGroupType('custom')
    } else if (group && group.id < 0) {
      setGroupType('default')
    }
  }, [group?.id])

  React.useEffect(() => {
    if (companyId && group) {
      fetchDriversInGroup()
    }
  }, [companyId, group])

  React.useEffect(() => {
    setSelectedGroup(driversInGroup.map((d) => d))
  }, [driversInGroup])

  const value = React.useMemo(() => {
    const isSelectedAny = selectedGroup.length > 0

    return {
      loading,
      driversInGroup,
      selectedGroup,
      isSelectedAny,
      addedIds,
      removedIds,
      toggleId,
      toggleAll,
      confirmEditGroupMembers,
      setGroupType,
    }
  }, [
    loading,
    driversInGroup,
    selectedGroup,
    addedIds,
    removedIds,
    confirmEditGroupMembers,

    setGroupType,
  ])

  return (
    <EditGroupsFormContext.Provider value={value}>
      {children}
    </EditGroupsFormContext.Provider>
  )
}

export const useEditGroups = (): EditGroupsFormContextType => {
  const ctx = React.useContext(EditGroupsFormContext)
  if (!ctx) {
    throw new Error(
      'useEditGroups must be used within a EditGroupsFormProvider',
    )
  }
  return ctx
}
